'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';

import { RecommendedProductsSection } from '@/components/RecommendedProductsSection';
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Grid,
    Heart,
    List,
    LucideIcon,
    Package,
    ShieldCheck,
    ShoppingBag,
    Sparkles,
    Trash2,
    Trash
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { ProductCard } from '@/components/ProductCard';
import { Button } from '@/components/ui/Button';
import { useWishlist } from '@/providers/WishlistProvider';
import { useWishlistProducts } from '@/hooks/useWishlistProducts';
import Image from 'next/image';
import { formatPrice } from '@/lib/utils/format';
import { Product } from '@/lib/Apis';
import { useBasePath } from '@/providers/BasePathProvider';
import { useNotification } from '@/providers/NotificationProvider';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

type SortOption = 'featured' | 'price-low' | 'price-high' | 'name' | 'availability';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'featured', label: 'Most loved' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name', label: 'Name A-Z' },
    { value: 'availability', label: 'Availability' },
];



export default function WishlistScreen() {
    const router = useRouter();
    const { buildPath } = useBasePath();
    const {
        products: wishlistItems,
        getWishlistCount,
        clearWishlist,
        refreshWishlist,
    } = useWishlist();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const notification = useNotification();

    const wishlistCount = getWishlistCount?.() ?? 0;
    const { products: wishlistProducts, isLoading, error } = useWishlistProducts(
        wishlistItems as string[]
    );


    const [selectedVariants, setSelectedVariants] = useState<Record<string, string | null>>({});
    const [onlyInStock, setOnlyInStock] = useState(false);
    const [sortOption, setSortOption] = useState<SortOption>('featured');

    useEffect(() => {
        if (wishlistProducts.length === 0) return;

        setSelectedVariants((prev) => {
            const next = { ...prev };
            let changed = false;
            wishlistProducts.forEach((p) => {
                const pid = p._id || p.id;
                const currentId = next[pid];

                if (p.variants?.length) {
                    const currentVariant = p.variants.find((v: any) => (v.id || v._id) === currentId);
                    const isCurrentInStock = currentVariant && (currentVariant.inventoryCount ?? 0) > 0;

                    // If onlyInStock is enabled or availability sort is active,
                    // and current selection is missing or out of stock,
                    // force select an in-stock variant.
                    if ((onlyInStock || sortOption === 'availability') && !isCurrentInStock) {
                        const inStockVariant = p.variants.find((v: any) => (v.inventoryCount ?? 0) > 0);
                        if (inStockVariant) {
                            const nextId = inStockVariant.id || inStockVariant._id || null;
                            if (nextId !== currentId) {
                                next[pid] = nextId;
                                changed = true;
                            }
                            return;
                        }
                    }

                    // Initial initialization: prefer in-stock even if filter is off
                    if (currentId === undefined) {
                        const inStockVariant = p.variants.find((v: any) => (v.inventoryCount ?? 0) > 0);
                        const defaultVariant = inStockVariant || p.variants[0];
                        const nextId = defaultVariant.id || defaultVariant._id || null;
                        next[pid] = nextId;
                        changed = true;
                    }
                }
            });
            return changed ? next : prev;
        });
    }, [wishlistProducts, onlyInStock, sortOption]);

    useEffect(() => {
        if (error) {
            notification.error(
                'Could not load wishlist',
                'We had trouble loading your saved products. Please refresh the page or try again shortly.'
            );
        }
    }, [error, notification]);

    const handleClearWishlist = async () => {
        try {
            await clearWishlist();
            await refreshWishlist();
            setIsConfirmModalOpen(false);
        } catch (err) {
            console.error('Error clearing wishlist:', err);
        }
    };

    const totalValue = useMemo(
        () => wishlistProducts.reduce((sum, product) => {
            const pid = product._id || product.id;
            const selectedVariantId = selectedVariants[pid];
            const variant = product.variants?.find((v) => (v.id || v._id) === selectedVariantId) ?? product.variants?.[0];
            return sum + (variant?.finalPrice ?? product.summary?.minPrice ?? 0);
        }, 0),
        [wishlistProducts, selectedVariants]
    );

    const totalSavings = useMemo(
        () =>
            wishlistProducts.reduce((sum, product) => {
                const pid = product._id || product.id;
                const selectedVariantId = selectedVariants[pid];
                const variant = product.variants?.find((v) => (v.id || v._id) === selectedVariantId) ?? product.variants?.[0];

                // Calculate savings if product/variant has discount
                if (variant?.isDiscounted) {
                    const regularPrice = variant.retailPrice ?? 0;
                    const finalPrice = variant.finalPrice ?? 0;
                    return sum + (regularPrice - finalPrice);
                }
                // Fallback to summary if no variants (though ProductCard usually relies on variants)
                if (product.summary?.hasDiscount) {
                    const maxPrice = product.summary?.maxPrice ?? 0;
                    const minPrice = product.summary?.minPrice ?? 0;
                    return sum + (maxPrice - minPrice);
                }
                return sum;
            }, 0),
        [wishlistProducts, selectedVariants]
    );

    const inStockCount = useMemo(
        () => wishlistProducts.filter((product) => (product.summary?.totalInventory ?? 0) > 0).length,
        [wishlistProducts]
    );

    // Helper to check if a product has any in-stock variant
    const hasInStockVariant = (product: any) => {
        if (product.variants?.length) {
            return product.variants.some((v: any) => (v.inventoryCount ?? 0) > 0);
        }
        return (product.summary?.totalInventory ?? 0) > 0;
    };

    // Helper to get total inventory across all variants or from summary
    const getProductInventory = (product: any) => {
        if (product.variants?.length) {
            return product.variants.reduce((sum: number, v: any) => sum + (v.inventoryCount ?? 0), 0);
        }
        return product.summary?.totalInventory ?? 0;
    };

    const processedProducts = useMemo(() => {
        let list = [...wishlistProducts];

        // Filter by in-stock - using variant-level check (NEE-111)
        if (onlyInStock) {
            list = list.filter((product) => hasInStockVariant(product));
        }

        switch (sortOption) {
            case 'price-low':
                list.sort((a, b) => (a.summary?.minPrice ?? 0) - (b.summary?.minPrice ?? 0));
                break;
            case 'price-high':
                list.sort((a, b) => (b.summary?.maxPrice ?? 0) - (a.summary?.maxPrice ?? 0));
                break;
            case 'name':
                list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                break;
            case 'availability':
                // Sort by: in-stock first, then by inventory quantity (NEE-110)
                list.sort((a, b) => {
                    const aInStock = hasInStockVariant(a);
                    const bInStock = hasInStockVariant(b);

                    // First prioritize in-stock items
                    if (aInStock && !bInStock) return -1;
                    if (!aInStock && bInStock) return 1;

                    // Then sort by inventory quantity
                    return getProductInventory(b) - getProductInventory(a);
                });
                break;
            case 'featured':
            default:
                list.sort((a, b) => (b.summary?.totalSold ?? 0) - (a.summary?.totalSold ?? 0));
                break;
        }

        return list;
    }, [wishlistProducts, onlyInStock, sortOption]);

    const emptyAfterFiltering = !isLoading && wishlistProducts.length > 0 && processedProducts.length === 0;

    return (
        <div className="bg-white">
            <div className="min-h-screen bg-white max-w-6xl mx-auto">
                <div className="container mx-auto px-4 py-8">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        <>
                                <div className="mb-8 rounded-3xl bg-gray-50/50 border border-gray-100 p-8">
                                    <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3">
                                                <div className="rounded-full bg-hsecondary/5 p-2.5">
                                                    <Heart className="h-6 w-6 text-hsecondary fill-hsecondary/10" />
                                                </div>
                                                <h1 className="text-3xl font-bold tracking-tight text-hsecondary">Your Wishlist</h1>
                                            </div>
                                            <p className="text-hmuted max-w-md text-sm leading-relaxed ml-1">
                                                Keep track of your favorite items for later. We'll keep them safe here until you're ready to make them yours.
                                            </p>
                                        </div>

                                        {wishlistCount > 0 && (
                                            <div className="flex items-center gap-8 px-6 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                                                <div>
                                                    <p className="text-xs font-semibold text-hmuted uppercase tracking-wider mb-0.5">Total Value</p>
                                                    <p className="text-2xl font-bold text-hsecondary">{formatPrice(totalValue)}</p>
                                                </div>
                                                <div className="h-8 w-px bg-gray-100" />
                                                <div>
                                                    <p className="text-xs font-semibold text-hmuted uppercase tracking-wider mb-0.5">Items</p>
                                                    <p className="text-2xl font-bold text-hsecondary">{wishlistCount}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-6">
                                    <div className="space-y-1">
                                        {onlyInStock && (
                                            <p className="text-sm text-hmuted">
                                                Showing items ready to ship
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:border-hprimary hover:text-hsecondary">
                                            <input
                                                type="checkbox"
                                                checked={onlyInStock}
                                                onChange={(event) => setOnlyInStock(event.target.checked)}
                                                className="h-4 w-4 rounded-sm border-slate-300 text-hsecondary focus:ring-hsecondary"
                                            />
                                            Only show in-stock
                                        </label>

                                        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-600">
                                            <span>Sort</span>
                                            <select
                                                value={sortOption}
                                                onChange={(event) => setSortOption(event.target.value as SortOption)}
                                                className="bg-transparent text-sm font-medium text-slate-700 outline-hidden"
                                            >
                                                {SORT_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>


                                        {wishlistCount > 0 && (
                                            <Button
                                                variant="ghost"
                                                className="text-sm font-semibold text-slate-500 hover:text-red-500"
                                                // onClick={handleClearWishlist}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setIsConfirmModalOpen(true);
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                Clear all
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {isLoading && (
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                                        {Array.from({ length: Math.min(wishlistCount || 3, 6) }).map((_, index) => (
                                            <div
                                                key={index}
                                                className="h-72 animate-pulse rounded-2xl border border-slate-200 bg-slate-100"
                                            />
                                        ))}
                                    </div>
                                )}

                                {!isLoading && wishlistCount === 0 && (
                                    <div data-testid="empty-wishlist" className="flex min-h-[30vh] items-center justify-center">
                                        <div className="text-center space-y-6 max-w-md">
                                            <div className="flex justify-center">
                                                <div className="rounded-full bg-gray-100 p-6">
                                                    <Heart className="h-12 w-12 text-hsecondary" />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <h2 className="text-2xl font-bold text-hsecondary">Your wishlist is empty</h2>
                                                <p className="text-hmuted">
                                                    Start adding products to your wishlist to see them here.
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap justify-center gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => router.push(buildPath('/shop'))}
                                                    className="rounded-xl border-2 border-hsecondary bg-hsecondary text-white px-6 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 hover:opacity-80"
                                                >
                                                    Discover products
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}


                                {!isLoading && processedProducts.length > 0 && (
                                    <>
                                        <motion.div
                                            data-testid="wishlist-grid"
                                            layout
                                            className="grid min-[300px]:grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3"
                                        >
                                            <AnimatePresence>
                                                {processedProducts.map((product) => (
                                                    <motion.div
                                                        key={product.id}
                                                        layout
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -20 }}
                                                        transition={{ duration: 0.2 }}
                                                    >
                                                        <ProductCard
                                                            product={product as Product}
                                                            onClickProduct={(p) => router.push(buildPath(`/products/${p._id}`))}
                                                            isFavorited
                                                            selectedVariantId={selectedVariants[product._id || product.id] ?? null}
                                                            onVariantChange={(variantId) => {
                                                                setSelectedVariants((prev) => ({
                                                                    ...prev,
                                                                    [product._id || product.id]: variantId,
                                                                }));
                                                            }}
                                                        />
                                                    </motion.div>
                                                ))}


                                            </AnimatePresence>
                                        </motion.div>
                                    </>
                                )}

                                {emptyAfterFiltering && (
                                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-12 text-center">
                                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200 text-gray-500">
                                            <Package className="h-8 w-8" />
                                        </div>
                                        <h3 className="mt-6 text-xl font-semibold text-hsecondary">
                                            Nothing matches those filters
                                        </h3>
                                        <p className="mt-2 max-w-md text-sm text-hmuted">
                                            Try showing out-of-stock items or adjust your sort order to revisit everything you&apos;ve saved.
                                        </p>
                                        <button
                                            type="button"
                                            className="mt-6 rounded-xl border-2 border-hprimary bg-white px-6 py-3 text-sm font-medium text-hsecondary hover:opacity-80 transition-colors"
                                            onClick={() => setOnlyInStock(false)}
                                        >
                                            Show all saved products
                                        </button>
                                    </div>
                                )}
                        </>

                        {/* Recommended Section - Always visible at bottom */}
                        <RecommendedProductsSection
                            title="Recommended for you"
                            subtitle=""
                            initialLimit={4}
                            className="pt-12 mt-12"
                        />
                    </motion.div>
                    <ConfirmModal
                        isOpen={isConfirmModalOpen}
                        onClose={() => {
                            setIsConfirmModalOpen(false);
                        }}
                        onConfirm={handleClearWishlist}
                        title="Clear Wishlist"
                        message="Are you sure you want to clear your wishlist? This action cannot be undone."
                        confirmText="Clear"
                        cancelText="Cancel"
                        variant="danger"
                    />
                </div>
            </div>
        </div>
    );
}
